import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useQueryClient } from 'react-query'
import { useWebsocketConnection } from 'providers/WebsocketProvider'
import { useAuth } from 'hooks/UseAuth'
import { optimisticUpdateFileInInfiniteLists, updateFileDetailCache } from 'hooks/queries/files/cacheUtils'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { AlmanacFileCalendarItem } from 'hooks/queries/calendar/types'
import { AlmanacFileShowItem } from 'hooks/queries/files/types'
import { GenerationProgress } from 'types'
import { type AlmanacCableConsumer, type AlmanacCableSubscription } from 'providers/CableConsumer/types'
import { isFileFromLatestBatch } from 'screens/ScheduledPosts/utils'

export interface DesignPreviewUpdate {
  op: 'preview_update'
  image_url: string
  last_rendered_version: number
  is_generating: boolean
  session_uuid: string
  duration: number | null
}

export interface DesignContentUpdate {
  op: 'content_update'
  design_content: object
}

export interface DesignProgressUpdate {
  op: 'progress_update'
  progress: GenerationProgress
  reference_image_url?: string
}

export const useDesignPreviewSync = (eventsData: AlmanacFileCalendarItem[]) => {
  const auth = useAuth()
  const websocketConnection = useWebsocketConnection()
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()
  const subscriptionsRef = useRef<Map<string, AlmanacCableSubscription>>(new Map())
  const activeConnectionRef = useRef<AlmanacCableConsumer | null>(null)

  const serializedDesignKeys = useMemo(() => {
    const designKeys = new Set<string>()
    eventsData.forEach((event) => {
      if (event?.asset_type !== 'Design' || !event?.key) return
      const needsPreview = event.image_url_generating || event.is_generating || !event.image_url
      if (needsPreview || (event.created_at && isFileFromLatestBatch(event))) {
        designKeys.add(event.key)
      }
    })

    return JSON.stringify(Array.from(designKeys).sort())
  }, [eventsData])

  const unsubscribeAll = useCallback(() => {
    subscriptionsRef.current.forEach((subscription) => {
      subscription.unsubscribe()
    })
    subscriptionsRef.current.clear()
    activeConnectionRef.current = null
  }, [])

  useEffect(() => {
    return () => {
      unsubscribeAll()
    }
  }, [unsubscribeAll])

  useEffect(() => {
    if (!auth || !websocketConnection) {
      unsubscribeAll()
      return
    }

    if (activeConnectionRef.current && activeConnectionRef.current !== websocketConnection) {
      unsubscribeAll()
    }

    activeConnectionRef.current = websocketConnection

    const designKeys = JSON.parse(serializedDesignKeys) as string[]
    const activeDesignKeys = new Set(designKeys)
    const subscriptions = subscriptionsRef.current

    subscriptions.forEach((subscription, key) => {
      if (activeDesignKeys.has(key)) return

      subscription.unsubscribe()
      subscriptions.delete(key)
    })

    designKeys.forEach((key) => {
      if (subscriptions.has(key)) return

      const subscription = websocketConnection.subscriptions.create(
        {
          channel: 'DesignPreviewChannel',
          key,
        },
        {
          received(data: DesignPreviewUpdate | DesignContentUpdate | DesignProgressUpdate) {
            if (data.op === 'preview_update') {
              optimisticUpdateFileInInfiniteLists(queryClient, workspaceId, key, (f) => ({
                ...f,
                image_url: data.image_url,
                image_url_generating: false,
                duration: data.duration || f.duration,
                is_generating: false,
              }))

              updateFileDetailCache<AlmanacFileShowItem>(queryClient, workspaceId, key, (file) => ({
                ...file,
                image_url: data.image_url,
                image_url_generating: false,
                duration: data.duration || file.duration,
                is_generating: false,
              }))
            }
            if (data.op === 'content_update') {
              optimisticUpdateFileInInfiniteLists(queryClient, workspaceId, key, (f) => ({
                ...f,
                design_content: data.design_content,
              }))

              updateFileDetailCache<AlmanacFileShowItem>(queryClient, workspaceId, key, (file) => ({
                ...file,
                design_content: data.design_content,
              }))
            }
            if (data.op === 'progress_update') {
              optimisticUpdateFileInInfiniteLists(queryClient, workspaceId, key, (f) => ({
                ...f,
                progress: data.progress,
                ...(data.reference_image_url ? { reference_image_url: data.reference_image_url } : {}),
              }))

              updateFileDetailCache<AlmanacFileShowItem>(queryClient, workspaceId, key, (file) => ({
                ...file,
                progress: data.progress,
                ...(data.reference_image_url ? { reference_image_url: data.reference_image_url } : {}),
              }))
            }
          },
        },
      )

      subscriptions.set(key, subscription)
    })
  }, [auth, websocketConnection, serializedDesignKeys, queryClient, workspaceId, unsubscribeAll])
}
